import { formatAsn } from '../lib/format'
import ChartFrame from './ChartFrame'
import WorldCanvas, { place } from './WorldCanvas'
import type { DnsechoRow, IpDetails } from '@shared/api'
import styles from './WorldPlot.module.css'

interface WorldPlotProps {
    rows: DnsechoRow[]
    self: IpDetails | undefined
}

interface Point {
    key: string
    x: number
    y: number
    detail: string
}

function toPoint(row: DnsechoRow, key: string): Point | undefined {
    const at = place(row.location)
    // geoip does not locate every address, and a missing position must not be
    // plotted at 0,0 -- the Gulf of Guinea is not a resolver location
    if (!at) return undefined

    const operator = row.asnOrg ?? row.isp ?? 'Unknown operator'
    return {
        key,
        x: at.x,
        y: at.y,
        detail: [row.ip, operator, formatAsn(row.asnNumber), row.cc].filter(Boolean).join(' · '),
    }
}

/**
 * Where the resolvers are, relative to the connection.
 *
 * This replaces a Leaflet map on OpenStreetMap tiles. The land outline is inline
 * geometry compiled into the bundle (see scripts/build-world-path.mjs), so the
 * plot makes no network requests at all -- which is the only way it renders for
 * visitors in mainland China, where the tile servers are blocked. It is also
 * smaller than Leaflet was on its own, before a single tile.
 *
 * Orientation, not evidence: it sits below the verdict and the tables because
 * "which countries" is a coarser question than "which organisations".
 */
export default function WorldPlot({ rows, self }: WorldPlotProps) {
    const resolvers = rows
        .map((row, i) => toPoint(row, `${row.ip}-${i}`))
        .filter((p): p is Point => p !== undefined)

    const you = place(self?.location)
    const unplaced = rows.length - resolvers.length

    /* The table view carries the resolver address as its own column, matching the
       Resolvers table -- "table view" should mean the same thing everywhere, and
       the IP is the row's identity. */
    const table = (
        <table>
            <thead>
                <tr>
                    <th scope="col">Resolver</th>
                    <th scope="col">Operator</th>
                    <th scope="col">Country</th>
                    <th scope="col">Coordinates</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={`${row.ip}-${i}`}>
                        <td>{row.ip}</td>
                        <td>{row.asnOrg ?? row.isp ?? '—'}</td>
                        <td>{row.cc ?? '—'}</td>
                        <td>
                            {row.location?.latitude !== undefined && row.location.longitude !== undefined
                                ? `${row.location.latitude.toFixed(2)}, ${row.location.longitude.toFixed(2)}`
                                : 'not located'}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    return (
        <ChartFrame
            title="Where your queries went"
            note={unplaced > 0
                ? `${unplaced} of ${rows.length} resolvers could not be located and are not plotted.`
                : 'Positions come from a geoip database and are city-level at best.'}
            aside={`${resolvers.length} plotted`}
            table={table}
        >
            <WorldCanvas label={`World map plotting ${resolvers.length} resolver locations`}>
                {/* a line from you to each resolver: the distance is the point */}
                {you && resolvers.map((p) => (
                    <line key={`link-${p.key}`} className={styles.link}
                        x1={you.x} y1={you.y} x2={p.x} y2={p.y} />
                ))}

                {resolvers.map((p) => (
                    <circle key={p.key} className={styles.resolver} cx={p.x} cy={p.y} r={7}>
                        <title>{p.detail}</title>
                    </circle>
                ))}

                {/* A ring, not a filled dot: the visitor is a position, while the
                    resolvers are the measurements sitting on it. */}
                {you && (
                    <circle className={styles.you} cx={you.x} cy={you.y} r={8}>
                        <title>You{self?.cc ? ` · ${self.cc}` : ''}</title>
                    </circle>
                )}
            </WorldCanvas>

            <p className={styles.legend}>
                <span className={styles.keyYou} /> your connection
                <span className={styles.keyResolver} /> resolver
            </p>
        </ChartFrame>
    )
}
